import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useId } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock } from 'lucide-react';
import { ActionButton } from '@/store/videoFeatures';
import { TimeInput } from '@/components/ui/time-input';

interface ButtonTimingCardProps {
  actionButton: Partial<ActionButton>;
  updateTimingProperty: (property: string | Record<string, any>, value?: any) => void;
  labelPrefix?: string;
}

export const ButtonTimingCard: React.FC<ButtonTimingCardProps> = ({
  actionButton,
  updateTimingProperty,
  labelPrefix = translateInterfaceText("o botão"),
}) => {
  useLanguage();
  const showThroughoutId = useId();
  const startTime = actionButton.timing?.start || '';
  const endTime = actionButton.timing?.end || '';

  // Quando o usuário digita algum tempo, desativa "Mostrar em todo vídeo".
  // Não forçamos o caminho inverso (TRUE quando vazio) porque agora o checkbox
  // é controlado manualmente — checar já limpa os inputs explicitamente.
  useEffect(() => {
    const hasTiming = !!(startTime || endTime);
    if (hasTiming && actionButton.timing?.showThroughout) {
      updateTimingProperty('showThroughout', false);
    }
  }, [startTime, endTime]);

  return (
    <Card className="border-border bg-card shadow-sm">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Tempo de Exibição")}</h3>
        </div>

        {/* Início e Término (lado a lado) */}
        <div className="grid grid-cols-2 gap-4">
          {/* Início */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{translateInterfaceText("Início")}</Label>
            <TimeInput
              value={startTime}
              onCommit={(v) => updateTimingProperty('start', v)}
              placeholder="00:00"
              className="h-[44px]"
            />
            <p className="text-xs text-muted-foreground">{translateInterfaceText("Início da headline")}</p>
          </div>

          {/* Término */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{translateInterfaceText("Término")}</Label>
            <TimeInput
              value={endTime}
              onCommit={(v) => updateTimingProperty('end', v)}
              placeholder={translateInterfaceText("Infinito")}
              className="h-[44px]"
            />
            <p className="text-xs text-muted-foreground">{translateInterfaceText("Deixe vazio para infinito")}</p>
          </div>
        </div>

        {/* Checkbox compacto */}
        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id={showThroughoutId}
            checked={actionButton.timing?.showThroughout || false}
            onCheckedChange={(checked) => {
              // Marcar "mostrar em todo vídeo" implica não ter janela de tempo
              // — limpa os inputs em um único update atômico pra evitar stale
              // closure (cada chamada individual re-spreda o timing original).
              if (checked) {
                updateTimingProperty({ showThroughout: true, start: '', end: '' });
              } else {
                updateTimingProperty('showThroughout', false);
              }
            }}
          />
          <Label htmlFor={showThroughoutId} className="text-sm font-normal cursor-pointer">
            {translateInterfaceText("Mostrar em todo vídeo")}</Label>
        </div>
      </CardContent>
    </Card>
  );
};
